import { AlertCircle, ArrowLeft, Loader2 } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { type BadgeTone, StatusBadge } from "@/components/admin/StatusBadge";
import { TopicDetailModal } from "@/components/admin/TopicDetailModal";
import {
  useParticipantCohorts,
  useParticipantEmailTimeline,
  useParticipantTopics,
} from "@/hooks/admin/emailSequence";
import { AdminLayout } from "@/layouts/AdminLayout";
import type {
  EmailLogItem,
  ParticipantCohortItem,
  ParticipantTopicItem,
  ScheduledEmailItem,
} from "@/types";

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDay(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const LOG_TONE: Record<string, BadgeTone> = {
  sent: "info",
  delivered: "success",
  bounced: "warning",
  complained: "warning",
  delivery_delayed: "neutral",
  failed: "warning",
};

const SCHEDULE_TONE: Record<string, BadgeTone> = {
  planned: "info",
  sent: "success",
  failed: "warning",
  cancelled: "neutral",
  skipped: "neutral",
  suppressed: "warning",
};

const ENROLLMENT_TONE: Record<string, BadgeTone> = {
  active: "success",
  completed: "neutral",
  dropped: "warning",
};

const ASSIGNMENT_TONE: Record<string, BadgeTone> = {
  not_started: "neutral",
  in_progress: "info",
  submitted: "success",
  reviewed: "success",
  resubmitted: "info",
  final_review: "info",
  completed: "success",
};

function humanize(value: string) {
  return value.replace(/_/g, " ");
}

function SentRow({ item }: { item: EmailLogItem }) {
  return (
    <li className="flex items-start justify-between gap-4 border-b border-gray-100 py-3 last:border-b-0">
      <div>
        <p className="text-sm font-medium text-gray-900">{item.subject}</p>
        <p className="text-xs text-gray-500">
          {item.template_id ?? "—"} · sent {formatDate(item.sent_at ?? item.created_at)}
        </p>
      </div>
      <StatusBadge tone={LOG_TONE[item.status] ?? "neutral"}>{item.status}</StatusBadge>
    </li>
  );
}

function UpcomingRow({ item }: { item: ScheduledEmailItem }) {
  return (
    <li className="flex items-start justify-between gap-4 border-b border-gray-100 py-3 last:border-b-0">
      <div>
        <p className="text-sm font-medium text-gray-900">{item.template_id}</p>
        <p className="text-xs text-gray-500">scheduled {formatDate(item.scheduled_for)}</p>
      </div>
      <StatusBadge tone={SCHEDULE_TONE[item.status] ?? "neutral"}>{item.status}</StatusBadge>
    </li>
  );
}

function CohortRow({ item }: { item: ParticipantCohortItem }) {
  const subtitle =
    item.status === "completed"
      ? `Completed · enrolled ${formatDay(item.enrolled_at)}`
      : `Week ${item.current_week} of ${item.total_weeks} · enrolled ${formatDay(item.enrolled_at)}`;
  return (
    <li className="flex items-start justify-between gap-4 border-b border-gray-100 py-3 last:border-b-0">
      <div>
        <p className="text-sm font-medium text-gray-900">{item.programme_name}</p>
        <p className="text-xs text-gray-500">{subtitle}</p>
      </div>
      <StatusBadge tone={ENROLLMENT_TONE[item.status] ?? "neutral"}>
        {humanize(item.status)}
      </StatusBadge>
    </li>
  );
}

function TopicRow({ item, onSelect }: { item: ParticipantTopicItem; onSelect: () => void }) {
  const meta = [`Week ${item.week_number}`, item.genre ?? null, item.programme_name].filter(
    Boolean
  );
  return (
    <li className="border-b border-gray-100 last:border-b-0">
      <button
        type="button"
        onClick={onSelect}
        className="flex w-full items-start justify-between gap-4 py-3 text-left transition-colors hover:bg-gray-50"
      >
        <div>
          <p className="text-sm font-medium text-gray-900">{item.topic_title}</p>
          <p className="text-xs text-gray-500">{meta.join(" · ")}</p>
        </div>
        <StatusBadge tone={ASSIGNMENT_TONE[item.status] ?? "neutral"}>
          {humanize(item.status)}
        </StatusBadge>
      </button>
    </li>
  );
}

function Card({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      {title && <h2 className="mb-2 text-sm font-semibold text-gray-900">{title}</h2>}
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-gray-500">{children}</p>;
}

function ScrollList({ children }: { children: React.ReactNode }) {
  return <ul className="scrollbar-thin max-h-[26rem] overflow-y-auto pr-1">{children}</ul>;
}

function Pending() {
  return (
    <div className="flex items-center justify-center p-8 text-gray-400">
      <Loader2 className="h-5 w-5 animate-spin" />
    </div>
  );
}

const TABS = [
  { key: "cohorts", label: "Cohorts" },
  { key: "topics", label: "Assigned topics" },
  { key: "emails", label: "Emails" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

export default function ParticipantDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>("cohorts");
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);

  const timeline = useParticipantEmailTimeline(userId);
  const cohorts = useParticipantCohorts(userId);
  const topics = useParticipantTopics(userId);

  // Only genuinely-upcoming (not-yet-sent) rows belong here. Already-sent rows are
  // shown — with their real delivery status — in the "Emails sent" panel, so
  // including them here duplicated them and displayed the weaker `sent` status.
  const upcoming = (timeline.data?.scheduled ?? []).filter((s) => s.status === "planned");
  const sent = timeline.data?.log ?? [];

  const counts: Record<TabKey, number | undefined> = {
    cohorts: cohorts.data?.length,
    topics: topics.data?.length,
    emails: timeline.data ? sent.length + upcoming.length : undefined,
  };

  return (
    <AdminLayout>
      <div className="flex h-full flex-col gap-4">
        <button
          type="button"
          onClick={() => navigate("/admin/users")}
          className="flex w-fit items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
        >
          <ArrowLeft className="h-4 w-4" /> Participants
        </button>

        {timeline.isPending && <Pending />}

        {timeline.isError && (
          <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4" />
            {timeline.error instanceof Error
              ? timeline.error.message
              : "Failed to load participant"}
          </div>
        )}

        {timeline.data && (
          <>
            <div className="rounded-lg border border-gray-200 bg-white p-5">
              <h1 className="text-lg font-semibold text-gray-900">
                {timeline.data.first_name || timeline.data.username}
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                {timeline.data.email ?? "no email on file"}
              </p>
            </div>

            <div className="flex gap-1 border-b border-gray-200">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  aria-current={tab === t.key}
                  className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                    tab === t.key
                      ? "border-primary text-primary"
                      : "border-transparent text-gray-500 hover:text-gray-800"
                  }`}
                >
                  {t.label}
                  {counts[t.key] !== undefined && (
                    <span className="ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-500">
                      {counts[t.key]}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {tab === "cohorts" && (
              <Card>
                {cohorts.isPending ? (
                  <Pending />
                ) : cohorts.data && cohorts.data.length > 0 ? (
                  <ScrollList>
                    {cohorts.data.map((c) => (
                      <CohortRow key={c.user_programme_id} item={c} />
                    ))}
                  </ScrollList>
                ) : (
                  <Empty>Not enrolled in any cohort</Empty>
                )}
              </Card>
            )}

            {tab === "topics" && (
              <Card>
                {topics.isPending ? (
                  <Pending />
                ) : topics.data && topics.data.length > 0 ? (
                  <ScrollList>
                    {topics.data.map((t) => (
                      <TopicRow
                        key={t.user_assignment_id}
                        item={t}
                        onSelect={() => setSelectedTopicId(t.topic_id)}
                      />
                    ))}
                  </ScrollList>
                ) : (
                  <Empty>No topics assigned yet</Empty>
                )}
              </Card>
            )}

            {tab === "emails" && (
              <div className="grid gap-4 md:grid-cols-2">
                <Card title="Emails sent">
                  {sent.length === 0 ? (
                    <Empty>No emails sent yet</Empty>
                  ) : (
                    <ScrollList>
                      {sent.map((item) => (
                        <SentRow key={item.id} item={item} />
                      ))}
                    </ScrollList>
                  )}
                </Card>

                <Card title="Upcoming">
                  {upcoming.length === 0 ? (
                    <Empty>Nothing scheduled</Empty>
                  ) : (
                    <ScrollList>
                      {upcoming.map((item) => (
                        <UpcomingRow key={item.id} item={item} />
                      ))}
                    </ScrollList>
                  )}
                </Card>
              </div>
            )}
          </>
        )}

        {selectedTopicId && (
          <TopicDetailModal
            topicId={selectedTopicId}
            open={!!selectedTopicId}
            onClose={() => setSelectedTopicId(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
}
